import { Outlet, useNavigate, useParams } from "react-router";
import { LogOut } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Stepper } from "@/features/profile/wizard/Stepper";
import { useCompleteness } from "@/features/profile/hooks";
import { useAuth } from "@/lib/auth/AuthProvider";

/**
 * The frame around onboarding.
 *
 * Deliberately without the main navigation: a draft account has nowhere else
 * to be, and a half-finished profile is the only task on the screen. One white
 * shell on the grey ground holds the progress, the steps and the form.
 */
export function OnboardingLayout() {
  const { step } = useParams();
  const { data: completeness, isLoading } = useCompleteness();
  const { session, signOut } = useAuth();
  const navigate = useNavigate();

  const done = completeness?.completed_count ?? 0;
  const total = completeness?.field_count ?? 0;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-dvh p-3 sm:p-4 lg:p-6">
      <div className="mx-auto w-full max-w-5xl rounded-xl bg-surface p-4 ring-1 ring-inset ring-line sm:p-6 lg:p-8">
        <header>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="truncate text-md font-semibold tracking-tight text-fg">
                First Gulf Exchange
              </p>
              <p className="truncate text-xs text-fg-subtle">
                {session?.user.full_name_en ?? "Complete your profile"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={<LogOut className="size-4" />}
              onClick={async () => {
                await signOut();
                navigate("/login", { replace: true });
              }}
            >
              Sign out
            </Button>
          </div>

          {/* Progress across the whole form, not just the current step. */}
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-300"
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Profile completion"
            />
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:mt-8 lg:grid-cols-[15rem_1fr] lg:gap-10">
          <div className="lg:sticky lg:top-6 lg:self-start">
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="rounded-md bg-surface-sunken p-1.5 lg:p-2">
                <Stepper currentKey={step ?? "personal"} completeness={completeness} />
              </div>
            )}
            {total > 0 ? (
              <p className="mt-3 hidden text-xs text-fg-subtle lg:block">
                {done} of {total} details filled in
              </p>
            ) : null}
          </div>

          <main className="min-w-0 pb-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
